import Logo from '@avanta/shared/assets/avanta-logo.svg';
import CircleCheck from '@avanta/shared/assets/circle-check.svg';
import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { ChevronRightIcon } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { SupportButton } from 'components/SupportButton';
import { ROUTES } from 'constants/routes';
import { SESSION_STORAGE } from 'constants/storage';
import { AppointmentSteps } from 'types/appointments';
import { getFromSessionStorage } from 'utils/storage';
import { type AppointmentsReserveFormData } from '../AppointmentsReserve/types';

export const AppointmentsCheckout = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const isSuccess = params.get('isSuccess');
  const checkoutAppointmentData = getFromSessionStorage<AppointmentsReserveFormData>(
    SESSION_STORAGE.CHECKOUT_APPOINTMENT_DATA
  );

  const handleOnNavigate = useCallback((): void => {
    const step = isSuccess ? AppointmentSteps.resume : AppointmentSteps.payment;
    const state = {
      checkoutData: checkoutAppointmentData,
      step,
    };
    navigate(ROUTES.APPOINTMENTS_RESERVE, { state });
  }, [checkoutAppointmentData, navigate, isSuccess]);

  useEffect(() => {
    if (checkoutAppointmentData && isSuccess) {
      const timeoutId = setTimeout(() => {
        handleOnNavigate();
      }, 5000);
      return (): void => clearTimeout(timeoutId);
    }
    handleOnNavigate();
  }, [checkoutAppointmentData, handleOnNavigate, navigate, isSuccess]);

  if (!isSuccess) {
    return <LoadingScreen fullScreen />;
  }

  return (
    <>
      <header className="py-4 am-lg:px-18">
        <div className="flex px-4 items-center justify-between">
          <Link to={ROUTES.HOME} className="flex items-center">
            <img src={Logo} alt="Avanta Mynd Logo" className="w-23 am-lg:w-28 h-auto" />
          </Link>
          <SupportButton />
        </div>
      </header>
      <div className="flex flex-col items-center am-lg:px-12 am-lg:pt-8 px-4 pt-22">
        <img src={CircleCheck} alt="Circle Check" className="size-28 text-teal-60 mb-4" />
        <div className="flex flex-col items-center gap-2 justify-center mb-4">
          <h3 className="text-base font-medium text-denim-500 leading-5">
            {t('AppointmentsCheckout.heading')}
          </h3>
          <p className="text-base text-gray-500 leading-5 text-center">
            {t('AppointmentsCheckout.body')}
          </p>
        </div>
        <Button
          variant="ghost"
          className="w-fit text-sm hover:text-teal-500 hover:bg-transparent font-normal text-teal-600 flex items-center gap-1 leading-4"
          onClick={handleOnNavigate}
        >
          {t('AppointmentsCheckout.cta')}
          <ChevronRightIcon className="size-5" />
        </Button>
      </div>
    </>
  );
};
